/**
 * PairDevicePage — guides the operator through pairing a tablet.
 * This page IS a real pairing actor (the same worker endpoint also
 * backs the setup-portal task and direct HMAC calls).
 *
 * Two-step flow:
 *   1. Operator opens the KDS app on the tablet, which calls
 *      /api/v1/devices/pair-init (anonymous, IP rate-limited) and
 *      displays an 8-character code (ABCD-1234).
 *   2. Operator types that code + a station name here — plus a kitchen
 *      location for multi-location merchants (KV-P1-PAIR-LOCATION).
 *      Submit POSTs /api/v1/admin/m/{merchantId}/pair-complete
 *      (merchant-session-authed; worker/src/routes/merchantAdmin.ts).
 *      On success the tablet picks up its device token via its
 *      /pair-status poll within a few seconds.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { PRODUCTS } from '@tovriktech/registry';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Field,
  StatusBadge,
} from '@tovriktech/ui';
import type { AdminLocation, AdminPairCompleteRequest } from '@kds/shared';
import { api, ApiError } from '../lib/api';

export interface PairDevicePageProps {
  merchantId: string;
  onDone: () => void;
  onCancel: () => void;
}

// The pairing-state vocabulary, derived from the existing local state only —
// no new fetch/poll. `expired` is set when the code-expired error branch fires,
// so the canonical StatusBadge reflects the live moment (waiting → pairing →
// expired/failed). All map onto the platform status vocabulary (§D).
type PairState = 'waiting' | 'pairing' | 'expired' | 'failed' | 'paired';

const PAIR_STATE_META: Record<PairState, { status: string; label: string }> = {
  waiting: { status: 'pending', label: 'Waiting for code' },
  pairing: { status: 'processing', label: 'Pairing…' },
  expired: { status: 'failed', label: 'Code expired' },
  failed: { status: 'failed', label: 'Pairing failed' },
  paired: { status: 'connected', label: 'Paired' },
};

export function PairDevicePage(props: PairDevicePageProps): ReactNode {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [expired, setExpired] = useState(false);
  // Kitchen locations (KV-P1-PAIR-LOCATION). `null` = still loading —
  // submit stays disabled so a fast operator can't race the fetch into
  // an unbound pair. Phase-7 devicesUi semantics, ported to the
  // canonical SPA surface:
  //   1 location  → auto-preselect + confirmation line
  //   2+          → dropdown, explicit choice REQUIRED
  //   0 / fetch error → legacy unbound pairing (fail-open; the worker's
  //     unbound_pair log is the ops signal)
  const [locations, setLocations] = useState<AdminLocation[] | null>(null);
  const [locationId, setLocationId] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLocations(null);
    setLocationId('');
    api
      .listLocations(props.merchantId)
      .then((r) => {
        if (cancelled) return;
        setLocations(r.locations);
        if (r.locations.length === 1) setLocationId(r.locations[0].id);
      })
      .catch(() => {
        // Fail open — same posture as devicesUi's picker: a locations
        // outage must not block pairing entirely.
        if (!cancelled) setLocations([]);
      });
    return () => {
      cancelled = true;
    };
  }, [props.merchantId]);

  const normalize = (raw: string): string => {
    const cleaned = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (cleaned.length === 8) return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
    return raw.toUpperCase();
  };

  // Visual-only: which canonical pairing state the badge reflects right now.
  const pairState: PairState = submitting
    ? 'pairing'
    : expired
      ? 'expired'
      : error
        ? 'failed'
        : 'waiting';
  const stateMeta = PAIR_STATE_META[pairState];

  async function onSubmit() {
    setError(null);
    setExpired(false);
    const c = normalize(code);
    if (!/^[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(c)) {
      setError('Enter 8 alphanumeric characters (e.g. ABCD-1234).');
      return;
    }
    if (!name.trim()) {
      setError('Give the station a name (e.g. "Grill", "Line 1").');
      return;
    }
    // Multi-location merchants MUST choose — an unbound device silently
    // misses location-keyed realtime broadcasts (KDS-P1-UNBOUND-PAIR).
    if (locations && locations.length > 1 && !locationId) {
      setError('Choose which location this tablet is for.');
      return;
    }
    setSubmitting(true);
    try {
      const input: AdminPairCompleteRequest = { code: c, device_name: name.trim() };
      if (locationId) input.location_id = locationId;
      await api.pairComplete(props.merchantId, input);
      // Success — the tablet will pick up its token within a few seconds
      // via the /pair-status poll. Bounce back to the device list.
      props.onDone();
    } catch (e) {
      // Truthful pair-error UX: branch on BOTH the HTTP status and the
      // machine `code`/`error` fields (jsonError envelope: { code, message,
      // error: message, ...extra }) so each failure class names its REAL
      // possibility. The status is the primary axis; `code` disambiguates
      // the overloaded 403 (a tablet-limit / subscription denial must never
      // read as a session or "invalid code" problem — see worker
      // middleware/subscription.ts + merchantSession.ts for the shapes).
      if (e instanceof ApiError) {
        const body = e.body as { error?: string; code?: string; limit?: number } | undefined;
        const errMsg = body?.error;
        const errCode = body?.code;
        if (e.status === 404 || errMsg === 'unknown code') {
          // The expensive real case: a tablet built for a DIFFERENT
          // environment/store shows a code whose row lives in another
          // env's pair_codes table, so THIS env 404s 'unknown code'.
          setError(
            "This code doesn't exist here. Double-check the code — and make sure the kitchen tablet is set up for THIS " +
              'environment/store (a tablet configured for a different environment shows codes that can’t pair here). ' +
              'Codes also expire 10 minutes after they appear.',
          );
        } else if (e.status === 409 || errMsg?.startsWith('already claimed')) {
          setError('This code has already been used. Restart pairing on the tablet for a new code.');
        } else if (e.status === 410 || errMsg === 'code expired') {
          setExpired(true);
          setError('This code has expired (codes are valid for 10 minutes). Restart pairing on the tablet.');
        } else if (errCode === 'device_limit_exceeded') {
          const lim = typeof body?.limit === 'number' ? ` (limit: ${body.limit})` : '';
          setError(
            `This merchant has reached its paired-tablet limit${lim}. Revoke an unused device from the device list, then try again.`,
          );
        } else if (
          errCode === 'subscription_inactive' ||
          errCode === 'subscription_missing' ||
          errCode === 'merchant_unknown'
        ) {
          setError(
            "This merchant's subscription isn't active for kitchen devices. Check the billing/subscription status, then try again.",
          );
        } else if (e.status === 401 || e.status === 403) {
          // Remaining auth/permission cases (unauthorized, forbidden,
          // forbidden_wrong_merchant, csrf_origin_rejected, no-KDS forbidden).
          setError(
            'Your session has expired or you don’t have permission to pair devices for this merchant. Refresh the page and sign in again.',
          );
        } else if (e.status >= 500) {
          setError('Couldn’t reach the pairing service — please try again in a moment.');
        } else {
          setError(`Pairing failed: ${errMsg ?? e.message}`);
        }
      } else {
        // Non-ApiError: a fetch TypeError / abort / non-JSON transport
        // failure — NEVER phrase this as an invalid code.
        setError('Couldn’t reach the pairing service — check your connection and try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-12">
      {/* Page header — Fraunces title + a supporting line (§B hierarchy). */}
      <header className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1.5">
          <h1 className="font-tvk-serif text-3xl font-semibold tracking-tight text-tvk-fg">
            Pair a {PRODUCTS.kitchen.display} tablet
          </h1>
          <p className="max-w-prose text-sm leading-relaxed text-tvk-fg-muted">
            Connect a kitchen-station tablet to this merchant in two steps. Enter the code shown on the
            tablet, give the station a name, and it will go live within a few seconds.
          </p>
        </div>
        <StatusBadge status={stateMeta.status}>{stateMeta.label}</StatusBadge>
      </header>

      {/* Step instructions — a designed card, not a bare list on white. */}
      <Card>
        <CardHeader>
          <CardTitle>How pairing works</CardTitle>
          <CardDescription>Follow these on the tablet at the kitchen station.</CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="space-y-4">
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-tvk-primary/10 text-xs font-semibold text-tvk-primary">
                1
              </span>
              <span className="leading-relaxed text-tvk-fg">
                Open the {PRODUCTS.kitchen.display} app on the tablet at the kitchen station.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-tvk-primary/10 text-xs font-semibold text-tvk-primary">
                2
              </span>
              <span className="leading-relaxed text-tvk-fg">
                Wait for the tablet to display an 8-character code. It looks like{' '}
                <code className="rounded-tvk-sm bg-tvk-surface-muted px-1.5 py-0.5 font-mono text-xs tracking-wider text-tvk-fg">
                  ABCD-1234
                </code>
                .
              </span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-tvk-primary/10 text-xs font-semibold text-tvk-primary">
                3
              </span>
              <span className="leading-relaxed text-tvk-fg">
                Type the code below and name this station.
              </span>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* The pairing moment — the form, with the live state echoed at the top. */}
      <Card className="mt-5">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle>Enter the pairing code</CardTitle>
            <StatusBadge status={stateMeta.status}>{stateMeta.label}</StatusBadge>
          </div>
          <CardDescription>The code is case-insensitive and valid for 10 minutes.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Field
            id="pair-code"
            label="Pair code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="ABCD-1234"
            autoComplete="off"
            className="font-mono text-lg uppercase tracking-[0.3em]"
            disabled={submitting}
          />
          <Field
            id="station-name"
            label="Station name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Grill, Line 1, Expediter, …"
            disabled={submitting}
          />
          {/* Location binding (KV-P1-PAIR-LOCATION). Dropdown only for
              multi-location merchants; single location is auto-bound and
              confirmed in plain copy; zero locations renders nothing
              (legacy unbound pairing). */}
          {locations && locations.length > 1 && (
            <div className="flex flex-col gap-1.5">
              <label htmlFor="pair-location" className="font-tvk-sans text-sm font-medium text-tvk-fg">
                Location
                <span className="ml-0.5 text-tvk-accent-strong" aria-hidden="true">*</span>
              </label>
              <select
                id="pair-location"
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                disabled={submitting}
                required
                // Mirrors the kit Input base look — the kit has no Select
                // primitive yet.
                className="flex h-10 w-full rounded-tvk-md border border-tvk-border bg-tvk-surface px-3 py-2 font-tvk-sans text-sm text-tvk-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tvk-primary focus-visible:ring-offset-2 focus-visible:ring-offset-tvk-canvas disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Choose a location…</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                    {l.address ? ` — ${l.address}` : ''}
                  </option>
                ))}
              </select>
              <p className="font-tvk-sans text-xs text-tvk-fg-muted">
                The tablet only receives orders for the location it is paired to.
              </p>
            </div>
          )}
          {locations && locations.length === 1 && (
            <p className="rounded-tvk-md bg-tvk-primary/5 p-3 text-xs leading-relaxed text-tvk-fg">
              This tablet will receive orders for:{' '}
              <span className="font-semibold">{locations[0].name}</span>
              {locations[0].address ? ` (${locations[0].address})` : ''}
            </p>
          )}
          {error && (
            <div
              role="alert"
              className="rounded-tvk-md border border-tvk-danger/30 bg-tvk-danger/10 p-3 text-xs leading-relaxed text-tvk-danger"
            >
              {error}
            </div>
          )}
          <div className="flex items-center justify-end gap-3 pt-1">
            {/* The CTA is disabled while the locations fetch is pending —
                say so, instead of leaving a dead button. The fetch is
                bounded (8s timeout in lib/api.ts), so this hint always
                resolves to either the picker or the unbound fallback. */}
            {locations === null && (
              <span role="status" className="mr-auto font-tvk-sans text-xs text-tvk-fg-muted">
                Loading locations…
              </span>
            )}
            <Button type="button" variant="ghost" size="sm" onClick={props.onCancel} disabled={submitting}>
              Cancel
            </Button>
            {/* Disabled while the locations fetch is in flight so a fast
                operator can't race it into an unbound pair. */}
            <Button
              type="button"
              variant="primary"
              onClick={onSubmit}
              loading={submitting}
              disabled={submitting || locations === null}
            >
              {submitting ? 'Pairing…' : 'Pair tablet'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <p className="mt-5 text-xs text-tvk-fg-muted">
        Pairing as merchant <span className="font-mono text-tvk-fg">{props.merchantId}</span>
      </p>
    </div>
  );
}
